import { useMutation } from '@apollo/client/react';
import { useContext } from 'react';
import {
	type AddProductsToProjectsMutation,
	type AddProductsToProjectsMutationVariables,
	type BatchMoveProjectItemsMutation,
	type BatchMoveProjectItemsMutationVariables
} from '../../__generated__/graphql-client-types';
import { ProjectSelectionContext } from '../../contexts/projects/project-selection/project-selection.context';
import { buildMoveCopyPayload, buildProjectGroupSources, getGroupsByProjectId } from '../../helpers/project/project.helper';
import { ADD_PRODUCTS_TO_PROJECTS } from '../../queries/project/batch-add-to-projects.queries';
import { BATCH_UPDATE_PROJECT_GROUPS, PROJECT_DETAILS_PAGE } from '../../queries/project/project-details.queries';
import { type ProjectDetailsPendingGroupItem, type ProjectInputWithName, type SelectableProjectGroup } from '../../types/project.types';
import { useProjectId } from './project-details.hooks';

export const useProjectActions = () => {
	const projectId = useProjectId();
	const { selection } = useContext(ProjectSelectionContext);
	const [batchUpdateItems, { loading: loadingBatchUpdateItems }] = useMutation<
		BatchMoveProjectItemsMutation,
		BatchMoveProjectItemsMutationVariables
	>(BATCH_UPDATE_PROJECT_GROUPS);
	const [addProductsToProjects, { loading: loadingCopyProductToProjects }] = useMutation<
		AddProductsToProjectsMutation,
		AddProductsToProjectsMutationVariables
	>(ADD_PRODUCTS_TO_PROJECTS);

	const moveOrCopyToProject = async (groups: SelectableProjectGroup[], isMoveView: boolean) => {
		const sources = buildProjectGroupSources(selection);
		const groupsByProject = getGroupsByProjectId(groups);

		await Promise.all(
			Object.entries(groupsByProject).map(async ([targetProjectId, projectGroups]) => {
				const payload = {
					projectId,
					targetProjectId: Number(targetProjectId) !== projectId ? Number(targetProjectId) : undefined,
					moveItems: isMoveView,
					sources,
					...buildMoveCopyPayload(projectGroups)
				};

				await batchUpdateItems({ variables: { input: payload } });
			})
		);
	};

	const copyProductToProjects = async (product: ProjectDetailsPendingGroupItem, projectInputs: ProjectInputWithName[]) => {
		const productInput = {
			product: {
				variantId: product.productVariantId,
				options: product.options?.map(({ id, value }) => ({ id, value })) || []
			},
			quantity: 1,
			subItems:
				product.subItems?.map((subItem) => ({
					product: {
						variantId: subItem.productVariantId,
						options: subItem.options?.map(({ id, value }) => ({ id, value })) || []
					},
					quantity: 1
				})) || []
		};

		await Promise.all(
			projectInputs.map(({ projectId: targetProjectId, cartId, groupId, quantity }) =>
				addProductsToProjects({
					variables: {
						input: {
							projects: [{ projectId: targetProjectId, cartId, groupId, quantity }],
							items: [
								{ ...productInput, quantity, subItems: productInput.subItems.map((subItem) => ({ ...subItem, quantity })) }
							]
						}
					},
					refetchQueries: [{ query: PROJECT_DETAILS_PAGE, variables: { projectId: targetProjectId } }],
					awaitRefetchQueries: true
				})
			)
		);
	};

	return {
		loadingBatchUpdateItems,
		loadingCopyProductToProjects,
		moveOrCopyToProject,
		copyProductToProjects
	};
};
